import React, { useState } from 'react';
import './TeamAddPage.scss';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inviteUser } from '../../../../shared/services/team/TeamService';
import { UserTeamData } from '../../../../shared/models/team/TeamResponse';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import useAddon from '../../../../shared/hooks/global/UseAddon';
import {
  AGENCY_HUB,
  TEAM,
  WIZARD_RETURN_AGENCY_HUB,
  WIZARD_RETURN_PARAM,
} from '../../../../shared/routes/path';
import GuestProjectsSelect from '../../../../shared/components/guest-projects-select/GuestProjectsSelect';

const TeamAddPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse, refetchAccountSilently } = useAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Opened from the Agency Hub (`?from=agency-hub`, as the project wizard does): go back there, both
  // after the invite and when the seat limit bounces the page.
  const returnPath =
    searchParams.get(WIZARD_RETURN_PARAM) === WIZARD_RETURN_AGENCY_HUB ? AGENCY_HUB : TEAM;
  const [apiError, setApiError] = useState<string | null>(null);
  const [role, setRole] = useState<{ name: string; type: string } | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const hasPermissionsAddon = useAddon('USER_PERMISSIONS');
  // Same id projektów: prawo edycji dla gościa jeszcze nie działa (bramka `requireWrite` jest
  // gotowa, ale żaden endpoint zapisu jej nie używa), więc nie ma czego wybierać — `canWrite`
  // idzie na serwer jako `false`.
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const isGuest = role?.type === 'GUEST';
  const roleOptions = [
    { name: t('admin'), type: 'ADMIN' },
    { name: t('user'), type: 'USER' },
    ...(hasPermissionsAddon ? [{ name: t('guest'), type: 'GUEST' }] : []),
  ];

  const errorDialog = (message: string = t('inviteErrorContent')) => {
    confirmDialog({
      draggable: false,
      message,
      header: t('inviteError'),
      defaultFocus: 'accept',
      acceptLabel: t('OK'),
    });
  };

  if (accountResponse && accountResponse.usersOnAccount >= accountResponse.maxUsers) {
    navigate(returnPath);
    return <></>;
  }

  const onSubmit = async (formData: any) => {
    if (!role) {
      setApiError(t('noRole'));
      return;
    }

    const guestProjects = projectIds.map((projectId) => ({ projectId, canWrite: false }));
    // Gość bez projektu zobaczyłby po zalogowaniu pustkę — backend odrzuca to tak samo.
    if (isGuest && guestProjects.length === 0) {
      setApiError(t('guestNeedsProject'));
      return;
    }

    const data = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      role: role.type,
      ...(isGuest ? { projects: guestProjects } : {}),
    } as UserTeamData;

    inviteUser(data)
      .then(async (isAdded) => {
        if (isAdded) {
          // The seat counter in the account context is what gates the next invite — refresh it, or it
          // keeps the value loaded at app start.
          await refetchAccountSilently();
          navigate(returnPath);
          return;
        }

        errorDialog();
      })
      .catch((err) => {
        const data = err.response?.data;
        // The seat limit is a server decision now (the disabled button only reads a cached count) —
        // the same dialog as a failed invite, with the numbers.
        if (data?.error === 'USERS_LIMIT_EXCEEDED') {
          errorDialog(t('usersLimitExceeded', { current: data.usersOnAccount, max: data.maxUsers }));
          return;
        }
        setApiError(data?.error);
      });
  };

  const itemTemplate = (option: any) => {
    return <div className="p-dropdown-item">{option.name}</div>;
  };

  return (
    <div className="team-add-page">
      <ConfirmDialog className="dialog-box dialog-box--error dialog-box--accept" />

      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <div className="form__row">
          <div className="form__label">{t('firstName')}:</div>
          <InputText
            {...register('firstName', {
              required: t('firstNameRequired'),
            })}
            type="text"
            className={errors.firstName || apiError ? 'form__invalid' : ''}
          />
          {errors.firstName && <span>{errors.firstName.message as string}</span>}
        </div>
        <div className="form__row">
          <div className="form__label">{t('lastName')}:</div>
          <InputText
            {...register('lastName', {
              required: t('lastNameRequired'),
            })}
            type="text"
            className={errors.lastName || apiError ? 'form__invalid' : ''}
          />
          {errors.lastName && <span>{errors.lastName.message as string}</span>}
        </div>
        <div className="form__row">
          <div className="form__label">{t('emailAddress')}:</div>
          <InputText
            {...register('email', {
              required: t('emailRequired'),
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('validEmailAddress'),
              },
            })}
            type="text"
            className={errors.email || apiError ? 'form__invalid' : ''}
          />
          {errors.email && <span>{errors.email.message as string}</span>}
        </div>
        <div className="form__row">
          <div className="form__label">{t('role')}:</div>
          <Dropdown
            value={role}
            placeholder={t('selectRole')}
            onChange={(e) => setRole(e.value)}
            options={roleOptions}
            className="dropdown"
            optionLabel="name"
            itemTemplate={itemTemplate}
          />
        </div>

        {isGuest && (
          <div className="form__row">
            <div className="form__label">{t('guestProjectsAccess')}:</div>
            <GuestProjectsSelect value={projectIds} onChange={setProjectIds} />
            {apiError && <span>{apiError}</span>}
          </div>
        )}

        <Button className="btn btn--sm" label={t('invite')} />
      </form>
      <div className="change-password-page__help">
        <div className="help-box">
          <label className="help-box__title">{t('inviteUser')}</label>
          <div>{t('inviteUserDesc')} </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAddPage;
